// Every file of the repository at one commit (the explorer's "All files").
// One recursive tree request; when GitHub truncates it (very large
// repositories), directories load one level at a time as they are opened.
//
// A complete listing never changes (it is of one commit), so it is kept in
// this browser (a runtime cache, runtime/cache.ts, in the signed-in
// account's IndexedDB): the explorer and Find file open on it without
// asking GitHub again. A truncated tree is not kept.
import { rest } from '../api/gh';
import { failureOf, type Failure } from '../api/failure';
import { identityUser } from '../api/identity';
import { cacheKey, hashInputs, openCache, type Cache } from '../runtime/cache';
import { idbBackend } from '../runtime/idb';

interface GitTreeEntry {
  path: string;
  type: 'blob' | 'tree' | 'commit';
  sha: string;
  size?: number;
  mode?: string;
}

interface GitTree {
  sha: string;
  truncated?: boolean;
  tree: GitTreeEntry[];
}

export interface TreeFile {
  sha: string;
  size: number | null;
}

export type DirState = 'unloaded' | 'loading' | 'loaded' | 'error';

export type TreeFetch = (path: string) => Promise<GitTree>;

/** A kept listing: each file's path, blob SHA and size. */
export type KeptTree = [path: string, sha: string, size: number | null][];

/** How long a listing is kept, and how many are (the least recently written go first). */
export const TREE_KEPT_MS = 30 * 24 * 60 * 60_000;
export const TREES_KEPT = 12;

const isKeptTree = (raw: unknown): raw is KeptTree =>
  Array.isArray(raw) && raw.every((e) => Array.isArray(e) && typeof e[0] === 'string' && typeof e[1] === 'string' && (e[2] === null || typeof e[2] === 'number'));

/** The cache of complete listings for `account` at `origin`, by `owner/repo@commit`. */
export function treeCache(origin: string, account: string, backend = idbBackend()): Cache<KeptTree> {
  return openCache<KeptTree>({
    name: 'repo-tree',
    key: cacheKey({ origin, account, schemaVersion: 1, inputsHash: hashInputs() }),
    decode: (raw) => (isKeptTree(raw) ? raw : null),
    backend,
    maxAgeMs: TREE_KEPT_MS,
    limit: TREES_KEPT,
  });
}

let shared: { account: string; cache: Cache<KeptTree> } | null = null;

/** The signed-in account's cache, held by a lease from its first use; null when nobody is signed in. */
function sharedTreeCache(): Cache<KeptTree> | null {
  const account = identityUser();
  if (!account || typeof location === 'undefined') return null;
  if (shared?.account !== account) {
    const cache = treeCache(location.origin, account);
    cache.hold();
    void cache.gc();
    shared = { account, cache };
  }
  return shared.cache;
}

const githubTree: TreeFetch = (p) => rest<GitTree>(p);

export class RepoTree {
  status: 'idle' | 'loading' | 'ready' | 'error' = $state('idle');
  /** Why the tree couldn't load (status `error`); null otherwise. */
  failure: Failure | null = $state(null);
  /** GitHub truncated the recursive tree: directories load as they are opened. */
  lazy = $state(false);
  /** Files known so far, by path. */
  files: Map<string, TreeFile> = $state.raw(new Map());
  /** Directories known so far (lazy mode), by path; '' is the root. */
  dirs: Map<string, { sha: string; state: DirState }> = $state.raw(new Map());
  /** File paths known so far (sorted). */
  paths: string[] = $derived([...this.files.keys()].sort());

  private loading: Promise<void> | null = null;

  constructor(
    public owner: string,
    public repo: string,
    /** Commit (or tree) SHA. */
    public commit: string,
    private fetchTree: TreeFetch = githubTree,
    /** Where complete listings are kept: the account's cache for GitHub's own trees, none for another fetch, unless given. */
    private cache: Cache<KeptTree> | null = fetchTree === githubTree ? sharedTreeCache() : null,
  ) {}

  private get key(): string {
    return `${this.owner}/${this.repo}`.toLowerCase() + `@${this.commit}`;
  }

  private url(sha: string, recursive: boolean): string {
    return `repos/${this.owner}/${this.repo}/git/trees/${sha}${recursive ? '?recursive=1' : ''}`;
  }

  /** Load the tree (once; a failed load can be retried). */
  load(): Promise<void> {
    if (this.status === 'ready') return Promise.resolve();
    if (this.loading) return this.loading;
    this.status = 'loading';
    this.loading = (async () => {
      try {
        const kept = await this.cache?.get(this.key);
        if (kept) {
          this.files = new Map(kept.value.map(([path, sha, size]) => [path, { sha, size }]));
          this.status = 'ready';
          this.failure = null;
          return;
        }
        const ticket = this.cache?.begin();
        const t = await this.fetchTree(this.url(this.commit, true));
        if (!t.truncated) {
          const files = new Map<string, TreeFile>();
          for (const e of t.tree) if (e.type === 'blob') files.set(e.path, { sha: e.sha, size: e.size ?? null });
          this.files = files;
          // Kept in the background: a listing that could not be stored is read from GitHub next time.
          if (this.cache && ticket) void this.cache.write({ [this.key]: [...files].map(([path, f]) => [path, f.sha, f.size]) }, ticket);
        } else {
          this.lazy = true;
          this.dirs = new Map([['', { sha: this.commit, state: 'unloaded' }]]);
          await this.expand('');
        }
        this.status = 'ready';
        this.failure = null;
      } catch (e) {
        this.status = 'error';
        this.failure = failureOf(e);
      } finally {
        this.loading = null;
      }
    })();
    return this.loading;
  }

  private expanding = new Map<string, Promise<void>>();

  /** In lazy mode, load one directory's entries (no-op once loaded or when not lazy; joins a load in flight). */
  expand(dir: string): Promise<void> {
    if (!this.lazy) return Promise.resolve();
    const running = this.expanding.get(dir);
    if (running) return running;
    const d = this.dirs.get(dir);
    if (!d || d.state === 'loaded') return Promise.resolve();
    const p = this.expandOnce(dir, d).finally(() => this.expanding.delete(dir));
    this.expanding.set(dir, p);
    return p;
  }

  private async expandOnce(dir: string, d: { sha: string; state: DirState }): Promise<void> {
    this.setDir(dir, { ...d, state: 'loading' });
    try {
      const t = await this.fetchTree(this.url(d.sha, false));
      const files = new Map(this.files);
      const dirs = new Map(this.dirs);
      for (const e of t.tree) {
        const path = dir ? `${dir}/${e.path}` : e.path;
        if (e.type === 'blob') files.set(path, { sha: e.sha, size: e.size ?? null });
        else if (e.type === 'tree' && !dirs.has(path)) dirs.set(path, { sha: e.sha, state: 'unloaded' });
      }
      dirs.set(dir, { sha: d.sha, state: 'loaded' });
      this.files = files;
      this.dirs = dirs;
    } catch {
      this.setDir(dir, { ...d, state: 'error' });
    }
  }

  /** Lazy mode: load every directory on the way to `path` (so a deep-linked file shows in the tree). */
  async reveal(path: string): Promise<void> {
    if (!this.lazy) return;
    const parts = path.split('/');
    parts.pop();
    let dir = '';
    await this.expand('');
    for (const name of parts) {
      dir = dir ? `${dir}/${name}` : name;
      await this.expand(dir);
    }
  }

  /** Directory paths known (lazy mode), without the root. */
  dirPaths(): string[] {
    return [...this.dirs.keys()].filter((d) => d !== '');
  }

  dirState(dir: string): DirState {
    if (!this.lazy) return 'loaded';
    return this.dirs.get(dir)?.state ?? 'loaded';
  }

  private setDir(dir: string, v: { sha: string; state: DirState }) {
    const dirs = new Map(this.dirs);
    dirs.set(dir, v);
    this.dirs = dirs;
  }
}
